<template>
  <div class="revision-submenu-buttons">
    <v-btn
      v-if="!revisionNotAppliedYet && (hasRevisionId || sideBySideView === 'datapeopleAnywhereApplyTemplate')"
      variant="text"
      class="text-capitalize"
      color="error"
      :loading="loading || isDebouncingOrSaving"
      @click="revertRevision()"
    >
      Restore Original
    </v-btn>
    <DpplAutoSizedButton
      v-if="$route.query.view === 'chrome'"
      class="text-capitalize"
      :loading="loading || isDebouncingOrSaving"
      @click="acceptRevision()"
    >
      Save and Close
    </DpplAutoSizedButton>
    <DpplAutoSizedButton
      v-else
      class="ml-2 text-capitalize"
      :disabled="loading || isDebouncingOrSaving"
      @click="acceptRevision()"
    >
      {{ submitBtn }}
    </DpplAutoSizedButton>
  </div>
</template>

<script>
import { useDatapeopleAnywhereEditor } from '@/stores/document/datapeopleAnywhereEditor';
import { mapActions, mapState } from 'pinia';
import { recruitRevert } from '@/api/recruit';
import { recruitJsonAjaxAnalyze } from '@/api/recruitJson';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useEditor } from '@/stores/editor/editor';
import DpplAutoSizedButton from '@/components/datapeople/DpplAutoSizedButton.vue';

export default {
  name: 'SideBySideSubMenuButtons',
  components: { DpplAutoSizedButton },
  props: { jobId: { type: [Number, String], default: null } },
  emits: ['tracking-event'],
  data() {
    return {
      revisionId: Number(this.$route.query?.sideRevision),
      loading: false,
    };
  },
  computed: {
    ...mapState(useDatapeopleAnywhereEditor, ['isDatapeopleAnywhereEditor']),
    ...mapState(useEditor, ['saveRequestStatus', 'sideBySideView']),
    isDebouncingOrSaving() {
      return this.saveRequestStatus === 'debouncing' || this.saveRequestStatus === 'saving';
    },
    submitBtn() {
      return this.sideBySideView === 'similarJobs'
        || this.sideBySideView === 'datapeopleAnywhereSimilarJob' ? 'Done' : 'Apply';
    },
    hasRevisionId() {
      return Boolean(this.$route.query?.sideRevision);
    },
    revisionNotAppliedYet() {
      return this.$route.query?.notAppliedYet === 'true';
    },
    revisionIdOrString() {
      return Number.isNaN(this.revisionId) ? this.$route.query?.sideRevision : this.revisionId;
    },
  },
  methods: {
    ...mapActions(useAppSnackbar, ['addErrorSnackbar']),
    ...mapActions(useDatapeopleAnywhereEditor, {
      datapeopleAnywhereAcceptSideBySide: 'acceptSideBySide',
      datapeopleAnywhereRevertSideBySide: 'revertSideBySide',
      datapeopleAnywhereSetSimilarJobId: 'setSimilarJobId',
    }),

    acceptRevision() {
      this.$emit('tracking-event', {
        action: 'acceptRevision',
        args: { id: this.jobId, revisionId: this.revisionIdOrString },
      });

      if (this.isDatapeopleAnywhereEditor) {
        this.datapeopleAnywhereSetSimilarJobId(null);
        return this.datapeopleAnywhereAcceptSideBySide();
      }

      return this.$router.replace({
        name: 'editor-view-reload', // reload to trigger a rescore
        params: { id: this.jobId },
        query: {
          ...this.$route.query,
          sideRevision: undefined,
          similarJobs: undefined,
          notAppliedYet: undefined,
        },
      });
    },

    async revertRevision() {
      this.$emit('tracking-event', {
        action: 'revertRevision',
        args: { id: this.jobId, revisionId: this.revisionIdOrString },
      });

      if (this.isDatapeopleAnywhereEditor) {
        this.datapeopleAnywhereRevertSideBySide();
        return;
      }

      try {
        this.loading = true;

        if (
          this.$route.query?.sideRevision === 'chromePrefilled'
          && this.$route.query?.chromeFirstRevision
        ) {
          const { chromeFirstRevision } = this.$route.query;

          await recruitRevert({
            jobId: this.jobId,
            revisionId: Number(chromeFirstRevision),
          });

          const { jobId } = await recruitJsonAjaxAnalyze({
            jobId: this.jobId,
            revisionId: Number(chromeFirstRevision),
          });

          await this.$router.replace({
            name: 'editor-view-reload',
            params: { id: jobId },
            query: {
              ...this.$route.query,
              sideRevision: undefined,
              chromeFirstRevision: undefined,
              notAppliedYet: undefined,
            },
          });
        } else {
          await recruitRevert({
            jobId: this.jobId,
            revisionId: this.revisionId,
          });

          const { jobId } = await recruitJsonAjaxAnalyze({
            jobId: this.jobId,
            revisionId: this.revisionId,
          });

          await this.$router.replace({
            name: 'editor-view-reload',
            params: { id: jobId },
            query: { ...this.$route.query, sideRevision: undefined, notAppliedYet: undefined },
          });
        }
      } catch (e) {
        if (e.response?.code === 'document_not_found') {
          this.addErrorSnackbar({ text: 'Revert template error: Document not found' });
          return;
        }

        this.addErrorSnackbar({ text: `Revert template error: ${e.message}` });
        throw e;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
